'use client';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y } from 'swiper/modules';
import { LeftArrow, RightArrow } from '../carousels';
import CarouselItem from './_CarouselItem';
import 'swiper/css';
import { useMounted } from '@repo/ui/src/hooks/useMounted';

const SLIDES_PER_VIEW_DEFAULT = 6;
const SLIDES_PER_VIEW_LG = 4;
const SLIDES_PER_VIEW_MD = 3;
const SLIDES_PER_VIEW_SM = 2;

const clampSlidesPerView = (categories, max) => Math.min(categories?.length ?? 1, max);

const CategorySlider = ({ categories, shouldOverlap, darkMode, _testID }) => {
  const { path } = useCurrentUrl();
  const swiperRef = useRef(null);

  const [isAnySelected, setIsAnySelected] = useState(false);
  const [sortedCategories, setSortedCategories] = useState(categories);
  const isMounted = useMounted();

  useEffect(() => {
    const index = categories.findIndex((x) => !!path && x?.link && path === x.link.link);
    setIsAnySelected(index >= 0);
    if (index >= 0) {
      setSortedCategories([categories[index], ...categories.filter((_x, i) => i !== index)]);
    }
  }, [path, categories]);

  const [visibleIndices, setVisibleIndices] = useState(null);

  const handleVisibility = useCallback((swiper) => {
    if (!swiper?.slides) return;
    const visibleSlides = new Set();
    swiper.slides.forEach((slide) => {
      if (slide.classList.contains('swiper-slide-visible')) {
        const i = Number(slide.dataset.swiperSlideIndex);
        if (Number.isInteger(i)) visibleSlides.add(i);
      }
    });
    setVisibleIndices(visibleSlides);
  }, []);

  const total = sortedCategories.length;

  if (!isMounted) {
    return null;
  }

  return (
    <section
      className={classNames(
        'px-8 sm:px-16 py-16 overflow-hidden',
        darkMode ? 'bg-neutral-800' : 'bg-transparent',
        shouldOverlap && 'relative bg-transparent z-20 left-0 -top-20 overlap',
      )}
      role="region"
      aria-label="carousel"
      data-test-id={`category_slider_${_testID}`}
    >
      <div className="relative">
        <LeftArrow
          darkMode={darkMode}
          arrowSize="35px"
          onClick={() => swiperRef.current?.slidePrev()}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10"
        />
        <Swiper
          modules={[A11y]}
          watchSlidesProgress
          grabCursor
          a11y={{ enabled: true }}
          className="!mx-10"
          slidesPerView={clampSlidesPerView(sortedCategories, SLIDES_PER_VIEW_SM)}
          loop={total > SLIDES_PER_VIEW_SM}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onAfterInit={handleVisibility}
          onSlideChange={handleVisibility}
          onBreakpoint={handleVisibility}
          breakpoints={{
            600: {
              slidesPerView: clampSlidesPerView(sortedCategories, SLIDES_PER_VIEW_MD),
              loop: total > SLIDES_PER_VIEW_MD,
            },
            768: {
              slidesPerView: clampSlidesPerView(sortedCategories, SLIDES_PER_VIEW_LG),
              loop: total > SLIDES_PER_VIEW_LG,
            },
            992: {
              slidesPerView: clampSlidesPerView(sortedCategories, SLIDES_PER_VIEW_DEFAULT),
              loop: total > SLIDES_PER_VIEW_DEFAULT,
            },
          }}
        >
          {sortedCategories?.map((item, index) => {
            const isInactive = visibleIndices && !visibleIndices.has(index);
            return (
              <SwiperSlide
                key={item.category}
                role="group"
                aria-label={`Category ${index + 1} of ${total}: ${item.category}`}
                aria-hidden={isInactive ? true : undefined}
              >
                <CarouselItem
                  {...item}
                  darkMode={darkMode}
                  index={index}
                  isAnySelected={isAnySelected}
                  tabIndex={isInactive ? -1 : undefined}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        <RightArrow
          darkMode={darkMode}
          arrowSize="35px"
          onClick={() => swiperRef.current?.slideNext()}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10"
        />
      </div>
    </section>
  );
};

export default CategorySlider;
